import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import { DEFAULT_CATEGORIES, UPLIFT_DEFAULT_CATEGORIES } from './constants';
import type {
	AgentRecommendationContext,
	BrowseAgentCategory,
	BrowseAgentExtraProps,
	BrowseAgentRuntimeProps,
} from './types';
import { getCategoryById } from './utils/getCategoryById';

export type BrowseAgentEntryPointParams = {
	/** Current content context for recommendations, when available. */
	recommendationContext?: AgentRecommendationContext;
	cloudId: string;
	product: string;
	categories?: (
		defaultCategories: readonly BrowseAgentCategory[],
	) => readonly BrowseAgentCategory[];
	initialCategory?: string;
	fetchPolicy?: PreloadFetchPolicy;
};

declare const browseAgentEntryPointType: EntryPoint<
	BrowseAgentEntryPointComponent,
	BrowseAgentEntryPointParams
>;

export type BrowseAgentEntryPoint = typeof browseAgentEntryPointType;

type BrowseAgentEntryPointComponent = EntryPointComponent<
	Record<string, any>,
	Record<string, any>,
	BrowseAgentRuntimeProps,
	BrowseAgentExtraProps
>;

export const browseAgentEntryPoint: BrowseAgentEntryPoint = createEntryPoint({
	root: JSResourceForInteraction<BrowseAgentEntryPointComponent>(() => import(/* webpackChunkName: "@atlaskit-internal_studio-browse-kit_browse-agent" */ "./BrowseAgent"), { moduleId: "1efb4a04", moduleName: "BrowseAgent", modulePath: "../platform/packages/ai-mate/studio-agent-browse/src/BrowseAgent.tsx", priority: "interaction", moduleId: 'studio-browse-kit_browse-agent',
		}),
	getPreloadProps: ({
		cloudId,
		product,
		categories,
		recommendationContext,
		initialCategory,
		fetchPolicy,
	}: BrowseAgentEntryPointParams) => {
		const defaultCategories = expVal('jira_browse_agent_modal_update', 'isEnabled', false)
			? UPLIFT_DEFAULT_CATEGORIES
			: DEFAULT_CATEGORIES;
		const resolvedCategories = categories?.(defaultCategories) ?? defaultCategories;
		const category = getCategoryById(resolvedCategories, initialCategory);

		return {
			entryPoints: {
				category: {
					entryPoint: category.entrypoint,
					entryPointParams: {
						categoryId: category.id,
						fetchPolicy,
						cloudId,
						product,
					},
				},
			},
			extraProps: {
				recommendationContext,
				categories: resolvedCategories,
				cloudId,
				initialCategory: category.id,
				product,
			},
		};
	},
});
